"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
function computeIncidentGroups(group, forwardGroupLookup) {
    const incidentGroups = [];
    group.faces.forEach(face => {
        const nonGroupIncidentFaces = face.incidentFaces.filter(incidentFace => forwardGroupLookup[incidentFace.index] !== group);
        nonGroupIncidentFaces.forEach(incidentFace => {
            const incidentGroup = forwardGroupLookup[incidentFace.index];
            if (incidentGroups.indexOf(incidentGroup) < 0) {
                incidentGroups.push(incidentGroup);
            }
        });
    });
    return incidentGroups;
}
function computeFaceSubtreeMarkers(crease) {
    if (crease.faces.length === 0) {
        return [];
    }
    // Init all faces an not part of a subtree to start.
    const subtreeMarkers = crease.faces.map(() => false);
    // Loop through faces and iteratively mark leaf nodes.
    let stopCondition = false;
    while (!stopCondition) {
        const facesFoundthisRound = [];
        crease.faces.forEach(face => {
            // Ignore this face if it's already been marked.
            if (subtreeMarkers[face.index]) {
                return;
            }
            // Find all non-tree incident faces.
            const nonTreeIncidentFaces = face.incidentFaces
                .filter((incidentFace) => !subtreeMarkers[incidentFace.index]);
            // If only one non-tree incident face exists, mark this face as part of a tree.
            if (nonTreeIncidentFaces.length <= 1) {
                facesFoundthisRound.push(face.index);
            }
        });
        facesFoundthisRound.forEach(index => {
            subtreeMarkers[index] = true;
        });
        // Stop when we haven't added any new faces.
        stopCondition = facesFoundthisRound.length === 0;
    }
    return subtreeMarkers;
}
function computeCycleFacesGroups(crease, subtreeMarkers) {
    const cycleFaces = crease.faces.filter(face => !subtreeMarkers[face.index]);
    if (cycleFaces.length === 0) {
        return [];
    }
    // Find connected groups within cycle faces.
    const groupIndices = crease.faces.map(face => face.index);
    let stopCondition = false;
    while (!stopCondition) {
        const changes = cycleFaces.map(face => {
            // Find all non-tree incident faces.
            const nonTreeIncidentFaces = face.incidentFaces
                .filter((incidentFace) => !subtreeMarkers[incidentFace.index]);
            const currentIndex = groupIndices[face.index];
            // Assign index of this face to smallest of current index and neighbor indices.
            nonTreeIncidentFaces.forEach(incidentFace => {
                if (groupIndices[incidentFace.index] < currentIndex) {
                    groupIndices[face.index] = groupIndices[incidentFace.index];
                }
            });
            // Return true if this face's index has changed.
            if (groupIndices[face.index] !== currentIndex) {
                return true;
            }
            return false;
        });
        stopCondition = changes.find(change => change === true) === undefined;
    }
    // Compile final groups list.
    const groups = {};
    // Filter group indices to only non-tree faces.
    groupIndices.filter((group, index) => !subtreeMarkers[index])
        .forEach((groupIndex, i) => {
        const face = cycleFaces[i];
        if (!groups[groupIndex]) {
            groups[groupIndex] = [];
        }
        groups[groupIndex].push(face);
    });
    return Object.values(groups);
}
function computeSubtreeFacesGroups(crease, subtreeMarkers) {
    const subtreeFaces = crease.faces.filter(face => subtreeMarkers[face.index]);
    return subtreeFaces.map(face => [face]);
}
function collapseCycles(crease) {
    // Mark all faces as part of trees or cycles.
    const subtreeMarkers = computeFaceSubtreeMarkers(crease);
    // Flood fill cycles to create groups.
    const cycleFacesGroups = computeCycleFacesGroups(crease, subtreeMarkers);
    // Compute tree groups.
    const subtreeFacesGroups = computeSubtreeFacesGroups(crease, subtreeMarkers);
    // Get all groups in dieline.
    const facesGroups = [...cycleFacesGroups, ...subtreeFacesGroups];
    // Add indices and adjacent groups.
    const groups = facesGroups.map((faces, i) => ({
        faces,
        index: i,
        incidentGroups: [],
    }));
    // Compute forward lookup from CreaseFace index to group.
    const forwardGroupLookup = crease.faces.map(() => groups[0]);
    groups.forEach(group => {
        group.faces.forEach(face => {
            forwardGroupLookup[face.index] = group;
        });
    });
    groups.forEach(group => {
        group.incidentGroups = computeIncidentGroups(group, forwardGroupLookup);
    });
    return { groups, forwardGroupLookup };
}
exports.collapseCycles = collapseCycles;
function computeSubtreeFaces(fixedFaceIndex, groups) {
    if (groups.length === 0) {
        return [];
    }
    // The value of subtreeFaces is different depnding on which face is fixed.
    // Start by throwing out any precomputed values.
    // Init subtreeFaces for each group.
    groups.forEach(group => {
        group.subtreeFaces = group.faces.slice();
    });
    // Keep track of groups we've analyzed so far.
    const visitedGroups = groups.map(() => false);
    // Find group containing fixed face, and mark it as visited.
    const fixedGroup = groups.find(group => group.faces.find(face => face.index === fixedFaceIndex));
    if (!fixedGroup) {
        throw new Error(`Unable to find fixed face index ${fixedFaceIndex} in groups:
            ${JSON.stringify(groups.map(group => group.faces.map(face => face.index)))}.`);
    }
    let stopCondition = false;
    while (!stopCondition) {
        const changes = groups.map(group => {
            // If already visited or if group is fixed, ignore.
            if (visitedGroups[group.index] || group === fixedGroup) {
                return false;
            }
            // Recursively move in from leaf nodes of tree and add their subtree to incident
            // groups closer to root of three.
            const unvisitedIncidentGroups = group.incidentGroups
                .filter(incidentGroup => !visitedGroups[incidentGroup.index]);
            if (unvisitedIncidentGroups.length > 1) {
                // Not a leaf node.
                return false;
            }
            unvisitedIncidentGroups.forEach(incidentGroup => {
                // Add this group's subtree faces to its neighbor.
                incidentGroup.subtreeFaces = incidentGroup.subtreeFaces
                    .concat(group.subtreeFaces);
            });
            // Mark as visited.
            visitedGroups[group.index] = true;
            return true;
        });
        stopCondition = changes.find(change => change === true) === undefined;
    }
    return groups;
}
exports.computeSubtreeFaces = computeSubtreeFaces;
function edgeCanBeRigidlyTransformed(edge, forwardGroupLookup) {
    const { leftFace, rightFace } = edge;
    // Ignore boundary edges.
    if (!leftFace || !rightFace) {
        return false;
    }
    // Get groups on either side of edge.
    const leftGroup = forwardGroupLookup[leftFace.index];
    const rightGroup = forwardGroupLookup[rightFace.index];
    // We can't do rigid transform if both faces belong to same group.
    // (We are in a cycle group).
    if (leftGroup === rightGroup) {
        return false;
    }
    // Check if edge is curved crease.
    if (edge.controlPoints.length > 0) {
        return false;
    }
    // Else we have a valid crease for rigid transform.
    return true;
}
exports.edgeCanBeRigidlyTransformed = edgeCanBeRigidlyTransformed;
function getGroupToTransform(group1, group2) {
    if (group1 === group2) {
        throw new Error('Groups are identical.');
    }
    if (!group1.subtreeFaces || !group2.subtreeFaces) {
        throw new Error('Must compute subtreeFaces of groups before querying TrasformPriority.');
    }
    if (group1.subtreeFaces.indexOf(group2.subtreeFaces[0]) >= 0) {
        return group2;
    }
    if (group2.subtreeFaces.indexOf(group1.subtreeFaces[0]) >= 0) {
        return group1;
    }
    throw new Error(`Neither of subtrees ${JSON.stringify(group1.subtreeFaces.map(face => face.index))} and 
        ${JSON.stringify(group2.subtreeFaces.map(face => face.index))} is parent of the other.`);
}
exports.getGroupToTransform = getGroupToTransform;
//# sourceMappingURL=TreeAnalyzer.js.map